import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from "@angular/router";
import { Observable, Subscription, first, firstValueFrom, filter, skip, take, timeout } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { Contract } from '../../objects/contract';
import { CLAUSE_SECTIONS, CLAUSE_LABELS_BY_KEYSECTION } from '../../objects/clause-sections';
import { CreateEntity } from '../create-entity';
import { environment } from '../../../environments/environment';

// Material imports
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';

// PrimeNG imports
import { AutoCompleteModule } from 'primeng/autocomplete';
import { EditorModule } from 'primeng/editor';

// Services
import { ContractService } from '../../service/entity-services/contract.service';
import { DatabaseAction, EntityType } from '../../objects/interface-library';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { ContractSheetsService } from '../../service/google-sheets/contract-sheets.service';
import { TimesheetSheetsService } from '../../service/google-sheets/timesheet-sheets.service';
import { SiteService } from '../../service/entity-services/site.service';
import { FileUploadService } from '../../service/files-and-attachments/file-upload.service';
import { RelationshipService } from '../../service/entity-data-services/relationship.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { DocumentAnalysisService, IntakeAnalysisResponse, PartyMatch, PartyMatchCandidate, AnalysisProgress, AI_MAX_FILE_BYTES, AI_FILE_TOO_LARGE_MESSAGE, describeAnalysisFailure } from '../../service/ai/document-analysis.service';
import { DocumentViewerSidenavComponent } from '../../entity-form-components/document-viewer-sidenav/document-viewer-sidenav.component';
import { EntityActionsSidenavComponent, EntityAction } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';

// Utilities
import { generateContractId } from '../../utils/id-generator';

// Dialog components
import { NewPartyDialogComponent } from '../new-party-dialog/new-party-dialog.component';
import { NewPersonDialogComponent } from '../new-person-dialog/new-person-dialog.component';
import { AIProcessingDialogComponent } from '../../shared/components/ai-processing-dialog/ai-processing-dialog.component';
import { MatDialogRef } from '@angular/material/dialog';

import { PartyLabelPipe, partyLabel } from '../../shared/pipes/party-label.pipe';
interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

/** Component properties backing the in-panel search box of each picker. */
type PickerFilterKey =
  | 'siteSearchFilter'
  | 'departmentSearchFilter'
  | 'regulatorySearchFilter'
  | 'otherPartiesSearchFilter'
  | 'subcontractorSearchFilter'
  | 'baaSearchFilter'
  | 'coiSearchFilter';

@Component({
  selector: 'app-new-contract-full-page',
  templateUrl: './new-contract.component.html',
  styleUrls: ['./new-contract.component.css'],
  standalone: true,
  imports: [
    PartyLabelPipe,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatProgressSpinner,
    MatOption,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatChipsModule,
    MatDividerModule,
    MatListModule,
    MatTooltipModule,
    MatRadioModule,
    AutoCompleteModule,
    EditorModule,
    DocumentViewerSidenavComponent,
    EntityActionsSidenavComponent
  ]
})
export class NewContractFullPageComponent extends CreateEntity implements OnInit, OnDestroy {
  private commonSheetsService = inject(CommonSheetsService);
  private contractSheetsService = inject(ContractSheetsService);
  private timesheetsSheetsService = inject(TimesheetSheetsService);
  private route = inject(ActivatedRoute);
  public sanitizer = inject(DomSanitizer);
  private fileUploadService = inject(FileUploadService);
  private relationshipService = inject(RelationshipService);
  private sidenavContentService = inject(SidenavContentService);
  private cdr = inject(ChangeDetectorRef);
  private documentAnalysisService = inject(DocumentAnalysisService);

  @ViewChild('actionsSidenav') actionsSidenav!: MatSidenav;

  // Environment configuration
  environment = environment;

  // Form management
  form: FormGroup;
  private formSubscription!: Subscription;

  currentContract!: Contract;

  /**
   * originalContract - Stores the original contract state for relationship tracking
   * Date: 2025-10-23
   * Used for: Tracking changes to relationships (directVendor, legalEntities, sites, etc.)
   *           when saving/updating contracts via the RelationshipService
   * UI Display: Previously displayed in a "Changes Made" card - removed from UI but
   *             functionality preserved for backend relationship updates
   */
  originalContract: any = {};
  
  // UI State
  showAdditionalDetails = false;
  showAISummary = false;
  isEditMode = false;
  isReplaceMode = false;
  sourceContractForReplace: Contract | null = null;

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Add New Party',
      icon: 'business',
      action: () => this.openAddPartyDialog(),
      disabled: false
    },
    {
      label: 'Add New Person',
      icon: 'person_add',
      action: () => this.openAddPersonDialog(),
      disabled: false
    }
  ]);

  // File handling properties moved to CreateEntity base class

  // AI and Summary
  aiProcessing = false;
  aiStatusMessage = 'Analyzing contract...';
  aiSummary = '';
  lowConfidenceFields: any[] = [];
  forceAI = false; // Checkbox to bypass AI cache and re-analyze

  // Intake AI Analysis
  intakeAnalysisResults: IntakeAnalysisResponse | null = null;
  partyMatchSelections: Map<string, { entityType: 'legalEntity' | 'vendor'; entityId: string }> = new Map(); // extractedName -> selected entity
  /** Extracted names the user created a brand-new party for, so the radio can show it. */
  partyMatchCreated: Set<string> = new Set();
  aiFilledFields: Set<string> = new Set(); // Track which fields were auto-filled by AI
  showPartyMatching = false;

  // Auto-populated fields display (for AI Analysis summary)
  autoPopulatedFieldsDisplay: { label: string; value: string; confidence: number }[] = [];

  // Calculated expiration date context from AI
  calculatedExpirationContext: {
    source: 'explicit' | 'calculated' | 'calculated_with_renewal' | 'evergreen' | 'expired' | 'unable_to_calculate' | null;
    message: string | null;
    confidence: 'high' | 'medium' | 'low' | null;
  } = { source: null, message: null, confidence: null };

  // Estimated status context from AI
  estimatedStatusContext: {
    value: string | null;
    confidence: 'high' | 'medium' | 'low' | null;
  } = { value: null, confidence: null };

  // Key section labels for display
  // Intake form keys off the AI keySections blob. Sourced from the shared
  // CLAUSE_SECTIONS registry (single source of truth) so the clause list can't
  // drift between the intake form, the read-only Provisions tab, and the AI dialog.
  keySectionLabels: { [key: string]: string } = CLAUSE_LABELS_BY_KEYSECTION;

  // S3 URL from AI upload - reused on save to avoid duplicate uploads
  aiUploadedS3Url: string | null = null;

  // AI processing dialog reference
  private aiProcessingDialogRef: MatDialogRef<AIProcessingDialogComponent> | null = null;

  /**
   * fieldChanges - Tracks field-level changes for display
   * Date: 2025-10-23
   * Used for: Previously displayed in a "Changes Made" UI card showing old vs new values
   * Status: UI card removed, but tracking logic preserved in trackChanges() method
   *         in case we want to re-enable it in the future
   */
  fieldChanges: FieldChange[] = [];
  
  // Static field data
  contractTypes: any[] = [];
  contractStatusTypes: any[] = [];
  renewalTypeLabels: any[] = [];
  riskCategories: any[] = [];
  contractLabels: any[] = [];
  sites: any[] = [];
  departments: any[] = [];
  legalEntities: any[] = [];
  regulatoryValues: any[] = [];
  databaseOrganizations: any[] = [];
  costCenters: any[] = [];

  // BAA/COI selector search filters
  baaSearchFilter = '';
  coiSearchFilter = '';

  // In-panel search filters for the multi-select pickers. Same pattern as the
  // BAA/COI selectors above: a mat-select can't take a matAutocomplete, so the
  // search box lives inside the panel and these back it.
  siteSearchFilter = '';
  departmentSearchFilter = '';
  regulatorySearchFilter = '';
  otherPartiesSearchFilter = '';
  subcontractorSearchFilter = '';

  /**
   * Stable, unfiltered party list for the multi-select pickers.
   *
   * `filteredParties` is narrowed IN PLACE by the Primary Party autocomplete's
   * filterParties(), so any picker that reads it inherits whatever was last
   * typed there — Other Parties and Sub-Contractors were silently showing a
   * subset. These pickers read this list instead and do their own filtering.
   */
  allPartiesList: any[] = [];

  saving = false;
  loadingContractData = false;

  constructor(
    public dialog: MatDialog,
    public router: Router,
    private fb: FormBuilder
  ) {
    super();

    // Ensure fieldChanges is always an array
    this.fieldChanges = [];
    
    // Initialize the form with validation
    this.form = this.fb.group({
      // Contract IDs - backend generates contractId for new contracts (Phase 1 API)
      contractId: [''], // Generated by backend for CREATE, required for EDIT
      contractNum: [''],  // Legacy partition key - may be deprecated
      version: [1],
      mainDocumentName: [''],
      nameOfContract: ['', Validators.required],
      directVendor: ['', Validators.required],
      
      // Basic contract info
      clientContractId: [''],
      contractType: [''],
      description: [''],
      
      // Status and classification
      status: ['draft'],  // Default to 'draft' - DynamoDB GSI requires non-null string
      contractLabel: [''],
      masterContract: [''],
      
      // Dates
      effectiveDate: [''],
      expirationDate: [''],
      lastReviewDate: [''],
      nextReviewDate: [''],
      executionDate: [''],
      internalExecutionDate: [''],
      externalExecutionDate: [''],
      
      // Terms (months/days)
      initialTerm: [''],
      renewalTerm: [''],
      renewalCycleLimit: [''],
      numRenewals: [''],
      noticePeriod: [''],
      reviewPeriod: [''],
      autorenew: [false],
      renewalType: [''],
      
      // Essential entities and people
      legalEntities: [[]],
      primaryOwner: [''],
      internalSignatory: [''],
      databaseOrg: [''],

      // Additional parties and sites
      parentParty: [''],
      otherParties: [[]],
      sites: [[]],
      departments: [[]],
      subcontractorVendorNums: [[]],
      
      // Additional people
      secondaryOwner: [''],
      tertiaryOwner: [''],
      yourContractCoordinator: [''],
      partiesSignatory: [''],
      partyContractCoordinator: [''],
      
      // Financial
      totalValue: [''],
      annualValue: [''],
      monthlyValue: [''],
      purchasePrice: [''],
      paymentTerms: [''],
      poNumber: [''],
      costCenter: [''],
      
      // Compliance
      embeddedBaa: [false],
      baaRequired: [false],
      coiRequired: [false],
      poRequired: [false],
      missingSignature: [false],
      baas: [[]],
      cois: [[]],
      directBaa: [''],
      directCoi: [''],
      
      // Risk and regulatory
      riskCategory: [''],
      regulatory: [[]],
      
      // Clinical/Admin
      clinicalOrAdmin: [''],

      // Key Contract Section Provisions (for risk analysis)
      termProvision: [''],
      indemnificationProvision: [''],
      terminationProvision: [''],
      nonCompeteProvision: [''],
      paymentTermsProvision: [''],
      compensationProvision: [''],
      liabilityLimitationsProvision: [''],
      insuranceRequirementsProvision: [''],
      confidentialityProvision: [''],
      disputeResolutionProvision: [''],
      costsFeesPricingProvision: [''],
      forceMajeureProvision: [''],
      governingLawProvision: [''],
      remediesProvision: [''],
      warrantyProvision: [''],

      // Grant Information
      grantFundNumber: [''],
      grantDocumentNumber: [''],
      grantProgramKey: [''],
      grantUse: [''],
      grantRestrictionsType: [''],
      grantProgramNumber: [''],
      revisedAwardValue: [''],

      // Value Calculations
      valuePatient: [''],
      valueHour: [''],
      valueDay: [''],
      valueMonth: [''],
      valueResidentPerYear: [''],
      valuePerDiem: [''],
      valueEncounter: [''],
      valueDelivery: [''],
      valueWorkshop: [''],
      valueClinic: [''],
      valueClass: [''],
      valueEvent: [''],
      valueProgram: [''],
      valueStudentPerWeek: [''],
      valueStudentPerMonth: [''],

      // SOCHS/AP Compensation
      apCompensationStructure: [''],
      acuteCareComp: [''],
      apSignOnBonusAmount: [''],
      apRetentionBonus: [''],
      apBaseSalary: [''],
      apHourlyRate: [''],
      apProductivityBonus: [''],
      apQualityBonus: [''],
      apShiftDifferential: [''],
      apCallPay: [''],
      apMalpracticeCoverage: [''],
      apRelocationAssistance: [''],
      apStudentLoanRepayment: [''],
      apTailCoverage: [''],
      apAnnualBonusPotential: [''],
      apStipend: [''],
      apRvuRate: [''],
      apMinimumGuarantee: [''],
      apBenefitsPackageValue: [''],
      apVacationDays: [''],
      apCmeAllowance: [''],
      apPartnershipTrack: [''],
      apNonCompeteRadius: [''],
      apNonCompeteDuration: [''],
      apWorkSchedule: [''],

      // HR Details
      hrCmeDues: [''],
      hrPtoTimeOff: [''],
      acuteCareSpecialty: [''],
      hrGroup: [''],
      hrProduct: [''],
      hrLawsonNumber: [''],
      hrPositionTitle: [''],
      hrDepartmentCode: [''],
      hrJobCode: [''],
      hrFte: [''],
      hrSupervisorName: [''],
      hrEmployeeId: [''],
      hrPayGrade: [''],
      hrPayType: [''],
      hrBenefitsEligible: [''],
      hrStartDate: [''],
      hrEndDate: [''],
      hrLocationCode: [''],
      hrCostCenter: [''],
      hrUnionStatus: [''],

      // Lease Management
      leaseStartDate: [''],
      leaseEndDate: [''],
      leaseMonthlyRent: [''],
      leaseAnnualRent: [''],
      leaseSquareFootage: [''],
      leasePricePerSqFt: [''],
      leaseSecurityDeposit: [''],
      leaseEscalationClause: [''],
      leaseEscalationPercentage: [''],
      leaseRenewalOptions: [''],
      leasePropertyAddress: [''],
      leaseType: [''],
      leaseLandlordName: [''],
      leaseOperatingExpenses: [''],
      leaseParkingSpaces: [''],
      leaseEarlyTerminationClause: ['']
    });
  }

  async ngOnInit() {
    // Register this component's sidenav toggle FIRST — before any awaited data
    // load. This sets detailSidenavActive=true so SecureHome hides the app-wide
    // nav. Must not sit after the try/finally below: if a data-load await throws,
    // the exception propagates past it and the registration is skipped, leaving
    // BOTH navbars visible (the documented two-navbar bug). The callback uses the
    // actionsSidenav ViewChild, which only needs to exist when the hamburger is
    // later clicked, so registering this early is safe.
    this.sidenavContentService.registerDetailSidenavToggle(() => {
      if (this.actionsSidenav) {
        this.actionsSidenav.toggle();
      }
    });

    // Set loading state immediately when component initializes
    this.loadingContractData = true;

    try {
      const contractNum = this.route.snapshot.paramMap.get('id');
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;

      // Load static field values
      await this.getStaticFieldValues();

      // Load autocomplete data in parallel, waiting for each cache to FINISH loading.
      //
      // These services stream their pages: getAllPaginatedDataProgressively() pushes every
      // partial page into the subject, so the old "first non-empty emission" read handed the
      // pickers page 1 — roughly 100 of cbhi's ~4,500 vendors. The missing rows never appeared
      // in a dropdown and never self-corrected, so a user could conclude a vendor didn't exist
      // and create a duplicate. settledCache() waits for the stream to go quiet instead, and
      // still returns promptly (not after a long timeout) when a service is genuinely empty —
      // an org with zero BAAs must not deadlock ngOnInit and leave the form spinning.
      [
        this.filteredUsers,
        this.filteredPeople,
        this.filteredSites,
        this.filteredLegalEntities,
        this.filteredParties,
        this.filteredContracts,
        this.filteredBaas,
        this.filteredCois,
      ] = await Promise.all([
        settledCache(this.allUsers$),
        settledCache(this.allPeople$),
        settledCache(this.allSites$),
        settledCache(this.allLegalEntities$),
        settledCache(this.allParties$),
        settledCache(this.allContracts$),
        settledCache(this.baaService.allBaas$ as Observable<any[]>),
        settledCache(this.coiService.allCois$ as Observable<any[]>),
      ]);

      // Snapshot the full party list before anything narrows filteredParties.
      this.allPartiesList = this.filteredParties;

      if (state?.replaceMode && state?.sourceContract) {
        // Replace mode: pre-fill form with source contract data but save as new version
        this.isReplaceMode = true;
        this.isEditMode = false; // Treat as CREATE so backend generates new IDs
        this.sourceContractForReplace = state.sourceContract;
        this.currentContract = { ...state.sourceContract };
        this.originalContract = {};
        // Clear both IDs — backend will generate fresh ones for the new version
        delete (this.currentContract as any).contractId;
        delete (this.currentContract as any).contractNum;
        // Bump version number
        const currentVersion = state.sourceContract.version || 1;
        this.currentContract.version = currentVersion + 1;
        this.populateFormWithContract(this.currentContract, true);
      } else if (contractNum) {
        // Load contract from service using ID
        this.isEditMode = true;
        await this.loadContractByNum(contractNum);
      } else if (state?.contract) {
        // Contract passed via router state
        this.isEditMode = true;
        this.currentContract = state.contract;
        this.originalContract = { ...state.contract };
        this.populateFormWithContract(this.currentContract, false);
      } else {
        this.isEditMode = false;
        // NEW API (Phase 1): Backend generates contractId on create
        // No need to pre-generate IDs - just start with empty form
      }

      // Workflow Request front-door: accept a file pre-selected by the intake dialog.
      const prefilledFile = state?.prefilledFile as File | undefined;
      if (prefilledFile instanceof File) {
        this.handleFileUpload(prefilledFile);

        const existingAnalysis = state?.intakeAnalysisResult;
        if (existingAnalysis?.status === 'complete' && existingAnalysis?.extraction) {
          // Dialog already ran AI analysis — reuse its results instead of calling AI again.
          console.log('%c[NewContract] Reusing AI analysis from workflow-request dialog',
            'color:#1976d2;font-weight:bold;', existingAnalysis);
          setTimeout(() => this.applyExistingAnalysis(existingAnalysis), 0);
        } else if (!state?.skipAi && prefilledFile.name.toLowerCase().endsWith('.pdf')) {
          // No existing analysis — run AI from scratch. skipAi means the dialog's
          // analysis already failed and the user chose the type by hand.
          setTimeout(() => this.processWithAI(), 0);
        }
      }

      // Subscribe to form changes for tracking edits
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentContract = { ...this.currentContract, ...changes };

        if (this.isEditMode) {
          this.trackChanges();
        }
      });

      // Subscribe to directVendor changes to filter party people and auto-populate parent party
      this.form.get('directVendor')?.valueChanges.subscribe((selectedParty) => {
        this.filterPartyPeople(selectedParty);
        this.updateParentPartyFromDirectVendor(selectedParty);
      });

    } catch (err) {
      // Don't let a data-load failure escape as an unhandled rejection — log it
      // so the underlying error is visible (it's what surfaced the nav bug).
      console.error('[NewContract] ngOnInit data load failed:', err);
    } finally {
      // Always turn off loading state when done (success or error)
      this.loadingContractData = false;

      // Force change detection to update the UI
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    // Unregister our sidenav toggle so the hamburger goes back to controlling the main sidenav
    this.sidenavContentService.unregisterDetailSidenavToggle();

    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  async loadContractByNum(contractId: string) {
    // Note: loadingContractData is set in ngOnInit, not here
    // to cover all async initialization operations

    try {
      // Get contract from service. settledCache, not firstValueFrom — the cache is
      // filled page by page, so a contract outside the first page fell through to
      // the "not found" branch and the edit bounced back to the contracts list.
      const contracts = await settledCache(this.contractService.visibleContracts$);
      const contract = contracts.find(c => c.contractNum === contractId);

      if (contract) {
        this.currentContract = contract;
        this.originalContract = { ...contract };
        this.populateFormWithContract(contract, false);
      } else {
        console.error('Contract not found with ID:', contractId);
        alert('Contract not found');
        this.router.navigate(['/securehome/contracts']);
      }
    } catch (error) {
      console.error('Error loading contract:', error);
      alert('Failed to load contract');
    }
  }

  async populateFormWithContract(contract: Contract, isNewVersion: boolean) {

    // Store original contract values for relationship tracking
    this.originalContract = {
      directVendor: contract.directVendor,
      parentParty: contract.parentParty,
      otherParties: Array.isArray(contract.otherParties) ? [...contract.otherParties] : [],
      legalEntities: Array.isArray(contract.legalEntities) ? [...contract.legalEntities] : [],
      sites: Array.isArray(contract.sites) ? [...contract.sites] : [],
      masterContract: contract.masterContract,
      directBaa: contract.directBaa,
      directCoi: contract.directCoi
    };

    this.form.patchValue({
      contractId: contract.contractId,
      contractNum: isNewVersion ? Date.now().toString() : contract.contractNum,
      version: isNewVersion ? (contract.version || 1) + 1 : (contract.version || 1),  // Increment if version
      mainDocumentName: contract.mainDocumentName ?? '',
      nameOfContract: contract.nameOfContract,
      directVendor: this.extractDirectVendorForUI(contract.directVendor),
      clientContractId: contract.clientContractId,
      contractType: contract.contractType,
      description: contract.description,
      status: contract.status,
      contractLabel: contract.contractLabel,
      masterContract: contract.masterContract,
      effectiveDate: contract.effectiveDate,
      expirationDate: contract.expirationDate,
      lastReviewDate: contract.lastReviewDate,
      nextReviewDate: contract.nextReviewDate,
      executionDate: contract.executionDate,
      internalExecutionDate: contract.internalExecutionDate,
      initialTerm: contract.initialTerm,
      renewalTerm: contract.renewalTerm,
      renewalCycleLimit: contract.renewalCycleLimit,
      numRenewals: contract.numRenewals,
      noticePeriod: contract.noticePeriod,
      reviewPeriod: contract.reviewPeriod,
      autorenew: contract.autorenew,
      renewalType: contract.renewalType,
      legalEntities: this.extractLegalEntitiesForUI(contract.legalEntities),
      // Owner fields - backend now returns hydrated objects, extract for form or use legacy fields
      primaryOwner: this.extractOwnerForForm((contract as any).primaryOwner || contract.primaryResponsibleParty),
      secondaryOwner: this.extractOwnerForForm((contract as any).secondaryOwner || contract.secondaryResponsibleParty),
      tertiaryOwner: this.extractOwnerForForm((contract as any).tertiaryOwner || contract.tertiaryResponsibleParty),
      internalSignatory: this.extractOwnerForForm(contract.internalSignatory),
      yourContractCoordinator: this.extractOwnerForForm(contract.yourContractCoordinator),
      databaseOrg: contract.databaseOrg,
      // parentParty is auto-populated from directVendor via valueChanges subscription
      otherParties: this.extractOtherPartiesForUI(contract.otherParties),
      sites: this.extractSitesForUI(contract.sites),
      departments: Array.isArray(contract.departments) ? contract.departments : [],
      subcontractorVendorNums: Array.isArray(contract.subcontractorVendorNums) ? contract.subcontractorVendorNums : [],
      partiesSignatory: this.extractPersonForForm(contract.partiesSignatory),
      partyContractCoordinator: this.extractPersonForForm(contract.partyContractCoordinator),
      externalExecutionDate: contract.externalExecutionDate,
      totalValue: contract.totalValue,
      annualValue: contract.annualValue,
      monthlyValue: contract.monthlyValue,
      purchasePrice: contract.purchasePrice,
      paymentTerms: contract.paymentTerms,
      poNumber: contract.poNumber,
      costCenter: contract.costCenter,
      embeddedBaa: contract.embeddedBaa,
      baaRequired: contract.baaRequired,
      coiRequired: contract.coiRequired,
      poRequired: contract.poRequired,
      missingSignature: contract.missingSignature,
      baas: Array.isArray(contract.baas) ? contract.baas : [],
      cois: Array.isArray(contract.cois) ? contract.cois : [],
      directBaa: this.extractDirectBaaForUI(contract.directBaa),
      directCoi: this.extractDirectCoiForUI(contract.directCoi),
      riskCategory: contract.riskCategory,
      regulatory: Array.isArray(contract.regulatory) ? contract.regulatory : [],
      clinicalOrAdmin: contract.clinicalOrAdmin,
      termProvision: contract.termProvision,
      indemnificationProvision: contract.indemnificationProvision,
      terminationProvision: contract.terminationProvision,
      nonCompeteProvision: contract.nonCompeteProvision,
      paymentTermsProvision: contract.paymentTermsProvision,
      compensationProvision: contract.compensationProvision,
      liabilityLimitationsProvision: contract.liabilityLimitationsProvision,
      insuranceRequirementsProvision: contract.insuranceRequirementsProvision,
      confidentialityProvision: contract.confidentialityProvision,
      disputeResolutionProvision: contract.disputeResolutionProvision,
      costsFeesPricingProvision: contract.costsFeesPricingProvision,
      forceMajeureProvision: contract.forceMajeureProvision,
      governingLawProvision: contract.governingLawProvision,
      remediesProvision: contract.remediesProvision,
      warrantyProvision: contract.warrantyProvision
    });

    // Handle existing file. Priority mirrors contract-form (read-only view):
    //   1. contractFileName/contractFile — bare filename, prepend s3Location
    //   2. linkToContract — only when it's a real S3 URL (ETL'd contracts)
    // Older ETL'd contracts may only carry linkToContract, so falling through to
    // it is what lets the viewer load on the edit page for those records.
    const docFileName = contract.contractFileName || contract.contractFile;
    const linkToContract = contract.linkToContract;
    const hasValidS3Link = !!linkToContract && typeof linkToContract === 'string'
      && linkToContract.includes('.s3.') && linkToContract.includes('amazonaws.com');

    if (docFileName) {
      this.loadDocument(environment.s3Location + docFileName);
    } else if (hasValidS3Link) {
      this.loadDocument(linkToContract!);
    }

    // Manually trigger party people filter if directVendor is set
    if (contract.directVendor) {
      this.filterPartyPeople(contract.directVendor);
    }
  }

  async filterPartyPeople(selectedParty?: any) {
    // Both caches are read once they've finished streaming — a first-non-empty
    // read here would narrow these pickers back to page 1 even after ngOnInit
    // had loaded them in full.
    const [allUsers, allPeople] = await Promise.all([
      settledCache(this.allUsers$),
      settledCache(this.allPeople$),
    ]);
    // Users for internal user fields; people for party signatory and party
    // contract coordinator.
    this.filteredUsers = allUsers;
    this.filteredPeople = allPeople;
  }

  createNewVersion() {
    if (this.currentContract) {
      this.isEditMode = false;  // Treat as create
      this.populateFormWithContract(this.currentContract, true);  // true = new version
      alert('Creating new version. Make your changes and click Save Contract.');
    }
  }


  // File Upload Methods moved to CreateEntity base class
  // (onDragOver, onDragLeave, onDrop, onFileSelected, handleFileUpload, removeFile, getFileNameFromUrl)

  /**
   * Override removeFile to also clear AI-related state
   */
  override removeFile(): void {
    super.removeFile();
    // Clear AI state when file is removed
    this.aiUploadedS3Url = null;
    this.intakeAnalysisResults = null;
    this.showPartyMatching = false;
    this.showAISummary = false;
    this.aiSummary = '';
    this.lowConfidenceFields = [];
    this.aiFilledFields.clear();
    this.partyMatchSelections.clear();
    this.partyMatchCreated.clear();
  }

  async processWithAI() {
    if (!this.uploadedFile) {
      alert('Please upload a contract file first');
      return;
    }

    // Only allow PDFs for AI analysis
    if (!this.uploadedFile.name.toLowerCase().endsWith('.pdf')) {
      alert('AI analysis only supports PDF files');
      return;
    }

    // Too big to be worth sending. The file stays attached and saves with the contract.
    if (this.uploadedFile.size > AI_MAX_FILE_BYTES) {
      alert(AI_FILE_TOO_LARGE_MESSAGE);
      return;
    }

    this.aiProcessing = true;
    this.aiStatusMessage = 'Uploading contract...';
    this.intakeAnalysisResults = null;
    this.showPartyMatching = false;
    this.partyMatchSelections.clear();
    this.partyMatchCreated.clear();
    this.aiFilledFields.clear();
    this.calculatedExpirationContext = { source: null, message: null, confidence: null };
    this.estimatedStatusContext = { value: null, confidence: null };

    // Open the AI processing modal dialog
    this.aiProcessingDialogRef = this.dialog.open(AIProcessingDialogComponent, {
      data: { message: 'Uploading contract...' },
      disableClose: true,
      panelClass: 'ai-processing-dialog'
    });

    this.cdr.detectChanges();

    try {
      // Step 1: Upload to S3 first (AI needs accessible URL)
      this.updateAIDialogMessage('Uploading to storage...');

      const s3Url = await this.uploadFileForAIAnalysis(this.uploadedFile);

      // Store S3 URL to reuse on save (avoid duplicate upload)
      this.aiUploadedS3Url = s3Url;

      // Step 2: Trigger AI analysis with progress callback
      const progressCallback = (progress: AnalysisProgress) => {
        this.aiStatusMessage = progress.message;
        this.updateAIDialogMessage(progress.message);
      };

      const response = await firstValueFrom(
        this.documentAnalysisService.triggerIntakeAnalysis(s3Url, this.uploadedFile.name, progressCallback, this.forceAI)
      );

      // console.log('[AI Intake] Full response:', response);
      // console.log('[AI Intake] Extraction:', response.extraction);
      // console.log('[AI Intake] Party Matches:', response.partyMatches);

      // Step 3: Process results
      if (response.status === 'complete') {
        this.intakeAnalysisResults = response;
        this.aiSummary = response.extraction?.summary || 'Contract analyzed successfully.';
        this.showAISummary = true;

        // Auto-fill form fields from extraction
        this.autoFillFromExtraction(response);

        // Handle party matches - auto-apply best matches and show UI for review
        if (response.partyMatches && response.partyMatches.length > 0) {
          this.showPartyMatching = true;
          this.preSelectHighConfidenceMatches(response.partyMatches);
          // Auto-apply best matches to form fields (the magic moment!)
          this.autoApplyBestPartyMatches(response.partyMatches);
        }

      } else if (response.status === 'failed') {
        // errorMessage is for support/logs and can be raw AWS text; show the mapped message.
        console.error('[AI Intake] Analysis failed:', response.errorCode, response.errorMessage);
        const failure = describeAnalysisFailure(response.errorCode);
        alert(response.analysisId ? `${failure.message}\n\nReference: ${response.analysisId}` : failure.message);
      }

    } catch (error) {
      console.error('[AI Intake] Processing error:', error);
      alert('Failed to analyze contract. Please try again or fill in the form manually.');
    } finally {
      // Close the dialog
      this.closeAIDialog();
      this.aiProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Apply AI analysis results that were already obtained by the workflow-request dialog.
   * Skips the S3 upload + AI call since those already happened.
   */
  private applyExistingAnalysis(response: any): void {
    this.intakeAnalysisResults = response;
    this.aiSummary = response.extraction?.summary || 'Contract analyzed successfully.';
    this.showAISummary = true;

    this.autoFillFromExtraction(response);

    if (response.partyMatches && response.partyMatches.length > 0) {
      this.showPartyMatching = true;
      this.preSelectHighConfidenceMatches(response.partyMatches);
      this.autoApplyBestPartyMatches(response.partyMatches);
    }

    this.cdr.detectChanges();
  }

  /**
   * Update the message shown in the AI processing dialog
   */
  private updateAIDialogMessage(message: string): void {
    if (this.aiProcessingDialogRef?.componentInstance) {
      this.aiProcessingDialogRef.componentInstance.updateMessage(message);
    }
    this.aiStatusMessage = message;
    this.cdr.detectChanges();
  }

  /**
   * Close the AI processing dialog
   */
  private closeAIDialog(): void {
    if (this.aiProcessingDialogRef) {
      this.aiProcessingDialogRef.close();
      this.aiProcessingDialogRef = null;
    }
  }

  /**
   * Upload file to S3 for AI analysis (different path than contract attachment)
   */
  private async uploadFileForAIAnalysis(file: File): Promise<string> {
    try {
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `intake/${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;

      const preSignedUrlResponse = await firstValueFrom(
        this.fileUploadService.getPresignedUrl(uniqueFileName)
      );
      const preSignedUrl = preSignedUrlResponse.presignedUrl;

      await firstValueFrom(
        this.fileUploadService.uploadFileToS3(preSignedUrl, file)
      );

      // Return the direct link (without query params)
      return preSignedUrl.split('?')[0];
    } catch (error) {
      console.error('S3 upload for AI failed:', error);
      throw error;
    }
  }

  /**
   * Auto-fill form fields from AI extraction results
   */
  private autoFillFromExtraction(response: IntakeAnalysisResponse): void {
    const extraction = response.extraction;
    if (!extraction) return;

    const updates: any = {};
    this.lowConfidenceFields = [];
    this.autoPopulatedFieldsDisplay = [];

    // Contract Name (auto-generated by AI: [Agreement Type] - [Vendor] - [Date])
    if (extraction.nameOfContract) {
      updates.nameOfContract = extraction.nameOfContract;
      this.aiFilledFields.add('nameOfContract');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Contract Name',
        value: extraction.nameOfContract,
        confidence: 0.95 // High confidence since AI auto-generates this
      });
    }

    // Effective Date
    if (extraction.effectiveDate?.normalized) {
      const confidence = extraction.effectiveDate.confidence;
      if (confidence >= 0.7) {
        updates.effectiveDate = new Date(extraction.effectiveDate.normalized);
        this.aiFilledFields.add('effectiveDate');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Effective Date',
          value: extraction.effectiveDate.extracted,
          confidence
        });
        if (confidence < 0.9) {
          this.lowConfidenceFields.push({
            field: 'Effective Date',
            suggestion: extraction.effectiveDate.extracted,
            confidence
          });
        }
      } else {
        this.lowConfidenceFields.push({
          field: 'Effective Date',
          suggestion: extraction.effectiveDate.extracted,
          confidence
        });
      }
    }

    // Expiration Date - prefer calculatedExpirationDate if available
    // Auto-fill if confidence >= 60% (low, medium, or high)
    if (extraction.calculatedExpirationDate) {
      const calcExp = extraction.calculatedExpirationDate;
      const confidenceNum = calcExp.confidence === 'high' ? 0.95
        : calcExp.confidence === 'medium' ? 0.8
        : calcExp.confidence === 'low' ? 0.6 : 0;

      // Auto-fill if we have a date and confidence >= 60%
      if (calcExp.date && confidenceNum >= 0.6) {
        updates.expirationDate = new Date(calcExp.date);
        this.aiFilledFields.add('expirationDate');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Expiration Date',
          value: new Date(calcExp.date).toLocaleDateString(),
          confidence: confidenceNum
        });
      }
    }
    // Fallback to overview.expirationDate if calculatedExpirationDate not present
    else if (extraction.overview?.expirationDate?.normalized) {
      const confidence = extraction.overview.expirationDate.confidence;
      // Auto-fill if confidence >= 60%
      if (confidence >= 0.6) {
        updates.expirationDate = new Date(extraction.overview.expirationDate.normalized);
        this.aiFilledFields.add('expirationDate');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Expiration Date',
          value: extraction.overview.expirationDate.extracted,
          confidence
        });
      }
    }

    // Estimated Status from AI
    // Auto-fill if confidence >= 60% (low, medium, or high)
    if (extraction.estimatedStatus) {
      const estStatus = extraction.estimatedStatus;
      const confidenceNum = estStatus.confidence === 'high' ? 0.95
        : estStatus.confidence === 'medium' ? 0.8
        : estStatus.confidence === 'low' ? 0.6 : 0;

      // Auto-fill if we have a valid status and confidence >= 60%
      // Check against contractStatusTypes loaded from sheets (case-insensitive)
      const matchedStatus = this.contractStatusTypes.find(
        s => s.toLowerCase() === estStatus.value?.toLowerCase()
      );
      if (matchedStatus && confidenceNum >= 0.6) {
        updates.status = matchedStatus;  // Use exact match from dropdown options
        this.aiFilledFields.add('status');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Contract Status',
          value: matchedStatus,
          confidence: confidenceNum
        });
      }
    }

    // Contract Type from AI (only present if confidence >= 0.8)
    if (extraction.contractType?.value) {
      // Match against contractTypes loaded from sheets (case-insensitive)
      const matchedContractType = this.contractTypes.find(
        t => t.toLowerCase() === extraction.contractType!.value.toLowerCase()
      );
      if (matchedContractType) {
        updates.contractType = matchedContractType;
        this.aiFilledFields.add('contractType');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Contract Type',
          value: matchedContractType,
          confidence: extraction.contractType.confidence
        });
      }
    }

    // Renewal Type from AI (only present if confidence >= 0.6)
    if (extraction.renewalType?.value) {
      // Match against renewalTypeLabels loaded from sheets (case-insensitive)
      const matchedRenewalType = this.renewalTypeLabels.find(
        t => t.toLowerCase() === extraction.renewalType!.value.toLowerCase()
      );
      if (matchedRenewalType) {
        updates.renewalType = matchedRenewalType;
        this.aiFilledFields.add('renewalType');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Renewal Type',
          value: matchedRenewalType,
          confidence: extraction.renewalType.confidence
        });
      }
    }

    // Renewal Term in Months from AI (only present if confidence >= 0.6)
    if (extraction.renewalTermMonths?.value !== undefined && extraction.renewalTermMonths.value !== null) {
      updates.renewalTerm = extraction.renewalTermMonths.value;
      this.aiFilledFields.add('renewalTerm');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Renewal Term',
        value: `${extraction.renewalTermMonths.value} months`,
        confidence: extraction.renewalTermMonths.confidence
      });
    }

    // Review Period from org configuration (days)
    // Always reliable when present - no confidence threshold needed
    if (extraction.reviewPeriod) {
      updates.reviewPeriod = extraction.reviewPeriod;
      this.aiFilledFields.add('reviewPeriod');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Review Period',
        value: `${extraction.reviewPeriod} days`,
        confidence: 1.0 // Config value - always reliable
      });
    }

    // Embedded BAA - AI detected a BAA as exhibit/addendum in the contract PDF
    // Boolean field - check the checkbox if AI found an embedded BAA
    if (extraction.embeddedBaa) {
      updates.embeddedBaa = true;
      this.aiFilledFields.add('embeddedBaa');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Embedded BAA',
        value: 'Yes (detected in document)',
        confidence: 1.0 // Binary detection - always reliable when true
      });
    }

    // Notice Period (days)
    if (extraction.terminationNoticePeriod?.normalizedDays) {
      const confidence = extraction.terminationNoticePeriod.confidence;
      if (confidence >= 0.7) {
        updates.noticePeriod = extraction.terminationNoticePeriod.normalizedDays;
        this.aiFilledFields.add('noticePeriod');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Notice Period',
          value: `${extraction.terminationNoticePeriod.normalizedDays} days`,
          confidence
        });
        if (confidence < 0.9) {
          this.lowConfidenceFields.push({
            field: 'Notice Period',
            suggestion: extraction.terminationNoticePeriod.extracted,
            confidence
          });
        }
      } else {
        this.lowConfidenceFields.push({
          field: 'Notice Period',
          suggestion: extraction.terminationNoticePeriod.extracted,
          confidence
        });
      }
    }

    // Payment Terms (net days) — stored as an integer string on the contract so
    // it flows to TimesheetSpec.netPaymentTerms and the invoice due date.
    // normalizedDays === 0 ("due upon receipt") is valid, so test for null/undefined.
    if (extraction.paymentTermsPeriod?.normalizedDays != null) {
      const confidence = extraction.paymentTermsPeriod.confidence;
      if (confidence >= 0.7) {
        updates.paymentTerms = String(extraction.paymentTermsPeriod.normalizedDays);
        this.aiFilledFields.add('paymentTerms');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Payment Terms',
          value: `${extraction.paymentTermsPeriod.normalizedDays} days`,
          confidence
        });
        if (confidence < 0.9) {
          this.lowConfidenceFields.push({
            field: 'Payment Terms',
            suggestion: extraction.paymentTermsPeriod.extracted,
            confidence
          });
        }
      } else {
        this.lowConfidenceFields.push({
          field: 'Payment Terms',
          suggestion: extraction.paymentTermsPeriod.extracted,
          confidence
        });
      }
    }

    // Initial Term (months from overview)
    if (extraction.overview?.initialTerm?.normalizedMonths) {
      const confidence = extraction.overview.initialTerm.confidence;
      if (confidence >= 0.7) {
        updates.initialTerm = extraction.overview.initialTerm.normalizedMonths;
        this.aiFilledFields.add('initialTerm');
        this.autoPopulatedFieldsDisplay.push({
          label: 'Initial Term',
          value: `${extraction.overview.initialTerm.normalizedMonths} months`,
          confidence
        });
        if (confidence < 0.9) {
          this.lowConfidenceFields.push({
            field: 'Initial Term',
            suggestion: extraction.overview.initialTerm.extracted,
            confidence
          });
        }
      } else {
        this.lowConfidenceFields.push({
          field: 'Initial Term',
          suggestion: extraction.overview.initialTerm.extracted,
          confidence
        });
      }
    }

    // Term Provision (from keySections.term)
    if (extraction.keySections?.term?.found && extraction.keySections.term.language) {
      updates.termProvision = extraction.keySections.term.language;
      this.aiFilledFields.add('termProvision');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Term Provision',
        value: extraction.keySections.term.language.length > 100
          ? extraction.keySections.term.language.substring(0, 100) + '...'
          : extraction.keySections.term.language,
        confidence: 0.9 // High confidence since it's extracted verbatim
      });
    }

    // Key Contract Sections - persist each extracted clause onto its contract
    // `*Provision` field. Driven by the shared CLAUSE_SECTIONS registry so adding
    // a clause is a one-line change there (no more per-clause if-blocks that drift).
    // `term` is handled above because it also surfaces in autoPopulatedFieldsDisplay;
    // the rest are background fields, not shown in that list.
    for (const clause of CLAUSE_SECTIONS) {
      if (clause.keySectionKey === 'term') continue;
      const section = (extraction.keySections as any)?.[clause.keySectionKey];
      if (section?.found && section.language) {
        (updates as any)[clause.contractField] = section.language;
      }
    }

    // Summary -> Description (Ray's mapping). The AI's natural-language summary
    // is the intended Description. Fall back to overview.contractPurpose only if
    // no summary was returned, so Description is never left blank.
    const descriptionText = extraction.summary || extraction.overview?.contractPurpose;
    if (descriptionText) {
      updates.description = descriptionText;
      this.aiFilledFields.add('description');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Description',
        value: descriptionText.length > 100
          ? descriptionText.substring(0, 100) + '...'
          : descriptionText,
        confidence: 0.85 // No explicit confidence for this field
      });
    }

    // Apply all updates at once
    if (Object.keys(updates).length > 0) {
      this.form.patchValue(updates);
    }
  }

  /**
   * Pre-select party matches with high confidence
   */
  private preSelectHighConfidenceMatches(partyMatches: PartyMatch[]): void {
    for (const match of partyMatches) {
      if ((match.matchConfidence === 'high' || match.matchConfidence === 'medium') && match.candidates.length > 0) {
        const topCandidate = match.candidates[0];
        // Get entity ID based on entity type
        const entityId = topCandidate.entityType === 'legalEntity'
          ? topCandidate.legalEntityNum
          : topCandidate.vendorNum;

        if (entityId) {
          this.partyMatchSelections.set(match.extractedName, {
            entityType: topCandidate.entityType,
            entityId: entityId
          });
        }
      }
      // For 'low' and 'none', don't pre-select
    }
  }

  /**
   * Auto-apply the best party matches to form fields immediately after AI analysis.
   * This provides the "magic moment" where Legal Entity and Primary Party are auto-filled.
   */
  private autoApplyBestPartyMatches(partyMatches: PartyMatch[]): void {
    let primaryPartyApplied = false;
    let legalEntityApplied = false;

    for (const match of partyMatches) {
      // Only auto-apply high or medium confidence matches
      if ((match.matchConfidence !== 'high' && match.matchConfidence !== 'medium') || match.candidates.length === 0) {
        continue;
      }

      const topCandidate = match.candidates[0];

      // Apply vendor as Primary Party (directVendor)
      if (match.extractedRole === 'vendor' && topCandidate.entityType === 'vendor' && !primaryPartyApplied) {
        const party = this.filteredParties.find(p => p.vendorNum === topCandidate.vendorNum);
        if (party) {
          this.form.patchValue({ directVendor: party });
          this.aiFilledFields.add('directVendor');
          primaryPartyApplied = true;

          // Add to auto-populated display
          this.autoPopulatedFieldsDisplay.push({
            label: 'Primary Party',
            value: topCandidate.vendorName || party.name || 'Unknown',
            confidence: topCandidate.similarity
          });

          // console.log('[AI Intake] Auto-applied Primary Party:', party.name);
        }
      }

      // Apply legal entity as Legal Entity (extractedRole can be 'facility' or 'legalEntity')
      if ((match.extractedRole === 'facility' || match.extractedRole === 'legalEntity') && topCandidate.entityType === 'legalEntity' && !legalEntityApplied) {
        const legalEntity = this.filteredLegalEntities.find(le => le.legalEntityNum === topCandidate.legalEntityNum);
        if (legalEntity) {
          const currentLegalEntities = this.form.get('legalEntities')?.value || [];
          this.form.patchValue({ legalEntities: [...currentLegalEntities, legalEntity] });
          this.aiFilledFields.add('legalEntities');
          legalEntityApplied = true;

          // Add to auto-populated display
          this.autoPopulatedFieldsDisplay.push({
            label: 'Legal Entity',
            value: topCandidate.legalEntityName || legalEntity.legalEntityName || 'Unknown',
            confidence: topCandidate.similarity
          });

          // console.log('[AI Intake] Auto-applied Legal Entity:', legalEntity.legalEntityName);
        }
      }
    }
  }

  /**
   * Handle party match selection from UI
   */
  onPartyMatchSelected(extractedName: string, entityType: 'legalEntity' | 'vendor', entityId: string | null): void {
    if (entityId) {
      this.partyMatchSelections.set(extractedName, { entityType, entityId });
    } else {
      this.partyMatchSelections.delete(extractedName);
    }
  }

  /**
   * Apply selected party matches to the form
   * - First vendor with role 'vendor' → Primary Party (directVendor)
   * - Additional vendors → Other Parties (otherParties)
   * - Entities with role 'facility' and type 'legalEntity' → Legal Entities
   */
  applyPartyMatches(): void {
    if (!this.intakeAnalysisResults?.partyMatches) return;

    const selectedLegalEntities: any[] = [];
    const selectedOtherParties: any[] = [];
    let primaryPartySet = false;

    // Check if directVendor is already set (from auto-apply)
    const currentDirectVendor = this.form.get('directVendor')?.value;
    if (currentDirectVendor && currentDirectVendor.vendorNum) {
      primaryPartySet = true;
    }

    for (const match of this.intakeAnalysisResults.partyMatches) {
      const selection = this.partyMatchSelections.get(match.extractedName);
      if (!selection) continue;

      if (selection.entityType === 'vendor') {
        // Find the party in filteredParties
        const party = this.filteredParties.find(p => p.vendorNum === selection.entityId);
        if (!party) continue;

        // First vendor match with role 'vendor' goes to Primary Party
        if (match.extractedRole === 'vendor' && !primaryPartySet) {
          this.form.patchValue({ directVendor: party });
          this.aiFilledFields.add('directVendor');
          primaryPartySet = true;
        } else {
          // Additional vendors go to Other Parties
          selectedOtherParties.push(party);
        }
      } else if (selection.entityType === 'legalEntity') {
        // Find the legal entity in filteredLegalEntities
        const legalEntity = this.filteredLegalEntities.find(le => le.legalEntityNum === selection.entityId);
        if (!legalEntity) continue;

        // Collect legal entities (typically for 'facility' role)
        selectedLegalEntities.push(legalEntity);
      }
    }

    // Apply collected legal entities to form
    if (selectedLegalEntities.length > 0) {
      const currentLegalEntities = this.form.get('legalEntities')?.value || [];
      // Avoid duplicates by checking legalEntityNum
      const existingNums = new Set(currentLegalEntities.map((le: any) => le.legalEntityNum));
      const newLegalEntities = selectedLegalEntities.filter(le => !existingNums.has(le.legalEntityNum));
      if (newLegalEntities.length > 0) {
        this.form.patchValue({ legalEntities: [...currentLegalEntities, ...newLegalEntities] });
        this.aiFilledFields.add('legalEntities');
      }
    }

    // Apply collected other parties to form
    if (selectedOtherParties.length > 0) {
      const currentOtherParties = this.form.get('otherParties')?.value || [];
      // Avoid duplicates by checking vendorNum
      const existingNums = new Set(currentOtherParties.map((p: any) => p.vendorNum));
      const newOtherParties = selectedOtherParties.filter(p => !existingNums.has(p.vendorNum));
      if (newOtherParties.length > 0) {
        this.form.patchValue({ otherParties: [...currentOtherParties, ...newOtherParties] });
        this.aiFilledFields.add('otherParties');
      }
    }

    this.showPartyMatching = false;
  }

  /**
   * Skip party matching and close the UI
   */
  skipPartyMatching(): void {
    this.showPartyMatching = false;
  }

  /**
   * Get CSS class for confidence level
   */
  /**
   * Get the unique ID for a party match candidate (for tracking and selection)
   */
  getCandidateId(candidate: PartyMatchCandidate): string {
    return candidate.entityType === 'legalEntity'
      ? candidate.legalEntityNum || ''
      : candidate.vendorNum || '';
  }

  /**
   * Get the display name for a party match candidate
   */
  getCandidateName(candidate: PartyMatchCandidate): string {
    return candidate.entityType === 'legalEntity'
      ? candidate.legalEntityName || ''
      : candidate.vendorName || '';
  }

  /**
   * Check if a candidate is currently selected for a given extracted party name
   */
  isCandidateSelected(extractedName: string, candidate: PartyMatchCandidate): boolean {
    const selection = this.partyMatchSelections.get(extractedName);
    if (!selection) return false;
    return selection.entityType === candidate.entityType && selection.entityId === this.getCandidateId(candidate);
  }

  getConfidenceClass(confidence: string): string {
    switch (confidence) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      case 'low': return 'confidence-low';
      default: return 'confidence-none';
    }
  }

  /**
   * Get key sections as array for template iteration
   */
  getKeySectionsArray(): { key: string; label: string; found: boolean; language?: string; pageReference?: string }[] {
    const keySections = this.intakeAnalysisResults?.extraction?.keySections;
    if (!keySections) return [];

    const sections: { key: string; label: string; found: boolean; language?: string; pageReference?: string }[] = [];
    const sectionKeys = Object.keys(this.keySectionLabels);

    for (const key of sectionKeys) {
      const section = (keySections as any)[key];
      sections.push({
        key,
        label: this.keySectionLabels[key],
        found: section?.found || false,
        language: section?.language,
        pageReference: section?.pageReference
      });
    }

    return sections;
  }

  /**
   * Format date for display in auto-populated fields
   */
  formatDateForDisplay(date: Date | string): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }

  /**
   * Open dialog to create a new party from extracted name
   */
  createPartyFromExtracted(extractedName: string): void {
    const dialogRef = this.dialog.open(NewPartyDialogComponent, {
      width: '600px',
      data: {
        parentEntity: null,
        prefilledName: extractedName
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.vendorNum) {
        // Shape matters: applyPartyMatches() reads `selection.entityType` and
        // `selection.entityId`. This used to store the bare vendorNum string, so
        // both reads came back undefined, the match fell through every branch,
        // and the party the user had just created was silently never attached to
        // the contract. TypeScript missed it because afterClosed() is
        // Observable<any>, so `result.vendorNum` is `any` and assignable to the
        // object type this Map declares.
        this.partyMatchSelections.set(extractedName, {
          entityType: 'vendor',
          entityId: result.vendorNum
        });
        // Mark it created so the radio stays visibly selected — the option is
        // rendered with a static [checked]="false" otherwise, which makes a
        // successful create look like nothing happened.
        this.partyMatchCreated.add(extractedName);

        // applyPartyMatches() resolves the selection by looking the vendorNum up
        // in filteredParties, so the refresh has to have LANDED before the user
        // can hit Apply — otherwise the lookup misses and we're back to silently
        // dropping the party. settledCache waits for the final page (the cache
        // streams ~45 of them on cbhi); taking the first emission would hand back
        // page 1 and shrink the pickers. Same treatment as openAddPartyDialog().
        this.partyService.refreshAllPartiesRecords().subscribe();
        settledCache(this.partyService.allParties$).then(parties => {
          this.filteredParties = parties;
          this.allPartiesList = parties;
          this.cdr.detectChanges();
        });
      }
    });
  }

  /**
   * Change Tracking Methods
   * Date: 2025-10-23
   * Purpose: Track field-level changes between original and current contract state
   * Status: UI card removed but functionality preserved for potential future use
   *
   * trackChanges() - Compares form values to originalContract and populates fieldChanges array
   * getFieldDisplayName() - Converts field keys to human-readable names
   *
   * Note: Currently called in form value changes subscription but not displayed in UI
   */
  trackChanges() {
    this.fieldChanges = [];

    Object.keys(this.form.controls).forEach(key => {
      const currentValue = this.form.get(key)?.value;
      const originalValue = this.originalContract[key];

      if (currentValue !== originalValue && (currentValue || originalValue)) {
        this.fieldChanges.push({
          field: this.getFieldDisplayName(key),
          oldValue: originalValue,
          newValue: currentValue
        });
      }
    });
  }

  getFieldDisplayName(fieldKey: string): string {
    const displayNames: { [key: string]: string } = {
      nameOfContract: 'Contract Name',
      directVendor: 'Primary Party',
      totalValue: 'Total Value',
      effectiveDate: 'Effective Date',
      expirationDate: 'Expiration Date',
      contractType: 'Contract Type',
      status: 'Status',
      // Add more mappings as needed
    };

    return displayNames[fieldKey] || fieldKey;
  }

  // Validation
  isFormValid(): boolean {
    // NEW API (Phase 1): contractId is generated by backend for new contracts
    // Only require nameOfContract and directVendor for form validation
    const requiredFields = ['nameOfContract', 'directVendor'];
    const basicValid = requiredFields.every(field => this.form.get(field)?.value);

    // For edit mode, also require contractId
    if (this.isEditMode) {
      return basicValid && !!this.form.get('contractId')?.value;
    }

    return basicValid;
  }

  getValidationMessage(): string {
    if (!this.form.get('nameOfContract')?.value) return 'Contract name is required';
    if (!this.form.get('directVendor')?.value) return 'Primary party is required';
    if (this.isEditMode && !this.form.get('contractId')?.value) return 'Contract ID is missing for edit';
    return '';
  }

  // Existing methods (simplified for brevity)
  async getStaticFieldValues() {
    // Load static data - replace with actual service calls
    this.contractTypes = await firstValueFrom(this.commonSheetsService.getContractTypes());
    this.contractStatusTypes = await firstValueFrom(this.commonSheetsService.getContractStatusTypes());
    this.renewalTypeLabels = await firstValueFrom(this.commonSheetsService.getRenewalTypeLabels());
    this.riskCategories = await firstValueFrom(this.commonSheetsService.getRiskCategories());
    this.contractLabels = await firstValueFrom(this.contractSheetsService.getContractLabels());
    this.departments = await firstValueFrom(this.commonSheetsService.getDepartments());
    this.regulatoryValues = await firstValueFrom(this.commonSheetsService.getRegulatoryValues());
    this.databaseOrganizations = await firstValueFrom(this.commonSheetsService.getDatabaseOrganizations());
    this.costCenters = await firstValueFrom(this.timesheetsSheetsService.getAllTimesheetCostCenterCodes());
  }



  loadContract() {
    this.loadingContractData = true;
    // Implement contract loading logic
    this.loadingContractData = false;
  }

  displayUserFn(user: any): string {
    return user && user.shortFriendlyName ? user.shortFriendlyName : '';
  }

  displayPersonFn(person: any): string {
    return person && person.fullName ? person.fullName : (person && person.friendlyName ? person.friendlyName : '');
  }

  displayPartyFn(party: any): string {
    return party && party.name ? party.name : '';
  }

  displayContractFn(contract: any): string {
    return contract && contract.nameOfContract ? contract.nameOfContract : '';
  }

  displayLegalEntityFn(entity: any): string {
    return entity && entity.legalEntityName ? entity.legalEntityName : '';
  }

  /**
   * Extracts legal entity for UI display from backend data
   * Date: 2025-10-24
   *
   * Backend hydrates all fields - this method just extracts the first legal entity
   * from the array for the single-select UI component.
   *
   * @param legalEntities - Can be:
   *   - Array of hydrated objects: [{legalEntityName: "...", ...}]
   *   - Single hydrated object: {legalEntityName: "...", ...}
   *   - null/undefined
   * @returns First legal entity object for autocomplete, or null
   */
  extractLegalEntityForUI(legalEntities: any): any {
    if (!legalEntities) {
      return null;
    }

    // If it's already a single object with legalEntityName, return as-is
    if (typeof legalEntities === 'object' && !Array.isArray(legalEntities) && legalEntities.legalEntityName) {
      return legalEntities;
    }

    // If it's an array, take the first item (backend should have already hydrated it)
    if (Array.isArray(legalEntities) && legalEntities.length > 0) {
      const firstItem = legalEntities[0];

      // Backend hydrated - check if it has nested legalEntityData structure
      if (typeof firstItem === 'object' && firstItem !== null) {
        // Nested structure: {legalEntityData: {...}, legalEntityNum: "..."}
        if (firstItem.legalEntityData && firstItem.legalEntityNum) {
          const foundEntity = this.filteredLegalEntities.find(e => e.legalEntityNum === firstItem.legalEntityNum);
          if (foundEntity) {
            return foundEntity;
          }
          return null;
        }

        // Direct object with legalEntityName
        if (firstItem.legalEntityName) {
          return firstItem;
        }
      }

      // If we got an ID string (backend hydration failed), look it up in filteredLegalEntities
      if (typeof firstItem === 'string') {
        const foundEntity = this.filteredLegalEntities.find(e => e.legalEntityNum === firstItem);
        if (foundEntity) {
          return foundEntity;
        }
        return null;
      }
    }

    return null;
  }

  /**
   * Extracts legal entities for UI display from backend data (multi-select version)
   * Converts array of legal entity data to array of full legal entity objects
   *
   * @param legalEntities - Can be:
   *   - Array of hydrated objects: [{legalEntityName: "...", ...}]
   *   - Array of ID strings: ["le123", "le456"]
   *   - null/undefined
   * @returns Array of legal entity objects for multi-select, or []
   */
  extractLegalEntitiesForUI(legalEntities: any): any[] {
    if (!legalEntities || !Array.isArray(legalEntities)) {
      return [];
    }

    // Map each legal entity to its full object
    const result = legalEntities.map((le) => {
      // Check if it's an object
      if (typeof le === 'object' && le !== null) {
        // Nested structure: {legalEntityData: {...}, legalEntityNum: "..."}
        if (le.legalEntityData && le.legalEntityNum) {
          const foundEntity = this.filteredLegalEntities.find(e => e.legalEntityNum === le.legalEntityNum);
          if (foundEntity) {
            return foundEntity;
          }
          return null;
        }

        // Direct object with legalEntityName
        if (le.legalEntityName) {
          return le;
        }
      }

      // It's an ID string - look it up
      if (typeof le === 'string') {
        const foundEntity = this.filteredLegalEntities.find(e => e.legalEntityNum === le);
        if (foundEntity) {
          return foundEntity;
        }
        return null;
      }

      return null;
    }).filter(le => le !== null); // Remove any nulls

    return result;
  }

  /**
   * Comparison function for mat-select to properly compare legal entity objects
   */
  compareLegalEntitiesFn(entity1: any, entity2: any): boolean {
    if (!entity1 || !entity2) return false;
    return entity1.legalEntityNum === entity2.legalEntityNum;
  }

  /**
   * Extracts directVendor (Primary Party) for UI display from backend data
   * Backend returns hydrated object: {vendorData: {...}, vendorNum: "..."}
   * UI needs the flat party object from filteredParties for autocomplete
   *
   * @param directVendor - Can be:
   *   - Hydrated object: {vendorData: {name: "...", ...}, vendorNum: "..."}
   *   - Flat object: {name: "...", vendorNum: "..."}
   *   - ID string: "vendorNum123"
   *   - null/undefined
   * @returns Party object for autocomplete, or null
   */
  extractDirectVendorForUI(directVendor: any): any {
    if (!directVendor) {
      return null;
    }

    // Extract vendorNum from various formats
    let vendorNum: string | null = null;

    if (typeof directVendor === 'string') {
      vendorNum = directVendor;
    } else if (typeof directVendor === 'object') {
      vendorNum = directVendor.vendorNum || null;
    }

    // Look up the party in filteredParties
    if (vendorNum && this.filteredParties) {
      const foundParty = this.filteredParties.find(p => p.vendorNum === vendorNum);
      if (foundParty) {
        return foundParty;
      }
    }

    // Fallback: if it's already a flat object with name, return as-is
    if (typeof directVendor === 'object' && directVendor.name) {
      return directVendor;
    }

    return null;
  }

  /**
   * Extracts otherParties for UI display from backend data (multi-select version)
   * Backend returns array of hydrated objects: [{vendorData: {...}, vendorNum: "..."}, ...]
   * UI needs array of flat party objects from filteredParties
   *
   * @param otherParties - Can be:
   *   - Array of hydrated objects: [{vendorData: {name: "...", ...}, vendorNum: "..."}, ...]
   *   - Array of flat objects: [{name: "...", vendorNum: "..."}, ...]
   *   - Array of ID strings: ["vendorNum123", "vendorNum456"]
   *   - null/undefined
   * @returns Array of party objects for multi-select, or []
   */
  extractOtherPartiesForUI(otherParties: any): any[] {
    if (!otherParties || !Array.isArray(otherParties)) {
      return [];
    }

    // Map each party to its full object from filteredParties
    const result = otherParties.map((party) => {
      // Extract vendorNum
      let vendorNum: string | null = null;

      if (typeof party === 'string') {
        vendorNum = party;
      } else if (typeof party === 'object' && party !== null) {
        vendorNum = party.vendorNum || null;
      }

      // Look up in filteredParties
      if (vendorNum && this.filteredParties) {
        const foundParty = this.filteredParties.find(p => p.vendorNum === vendorNum);
        if (foundParty) {
          return foundParty;
        }
      }

      // Fallback: if it's already a flat object with name, return as-is
      if (typeof party === 'object' && party !== null && party.name) {
        return party;
      }

      return null;
    }).filter(p => p !== null);

    return result;
  }

  /**
   * Comparison function for mat-select to properly compare party objects
   */
  comparePartiesFn(party1: any, party2: any): boolean {
    if (!party1 || !party2) return false;
    return party1.vendorNum === party2.vendorNum;
  }

  /**
   * Updates the parentParty field when directVendor (Primary Party) changes.
   * If the selected party has a parentVendor, auto-populate the Parent Party field.
   * If no parent, clear the Parent Party field.
   */
  updateParentPartyFromDirectVendor(selectedParty: any): void {
    if (!selectedParty || typeof selectedParty !== 'object') {
      // No party selected, clear parent party
      this.form.get('parentParty')?.setValue(null);
      return;
    }

    // Check if the selected party has a parentVendor
    const parentVendorNum = selectedParty.parentVendor;
    if (parentVendorNum) {
      // Look up the parent in the FULL party list, not filteredParties. This runs
      // immediately after the user picks a Primary Party from the autocomplete, so
      // filteredParties still holds only what matched their typing — a parent whose
      // name doesn't contain those same letters would be "not found" and the Parent
      // Party field would silently clear.
      const parentParty = this.allPartiesList.find(p => p.vendorNum === parentVendorNum);
      if (parentParty) {
        this.form.get('parentParty')?.setValue(parentParty);
      } else {
        // Parent exists but not found in list - clear field
        this.form.get('parentParty')?.setValue(null);
      }
    } else {
      // No parent vendor, clear the field
      this.form.get('parentParty')?.setValue(null);
    }
  }

  /**
   * Extracts sites for UI display from backend data
   * Converts array of site IDs to array of full site objects
   *
   * @param sites - Can be:
   *   - Array of hydrated objects: [{siteName: "...", ...}]
   *   - Array of ID strings: ["site123", "site456"]
   *   - null/undefined
   * @returns Array of site objects for autocomplete, or []
   */
  extractSitesForUI(sites: any): any[] {
    if (!sites || !Array.isArray(sites)) {
      return [];
    }

    // Map each site to its full object
    const result = sites.map((site) => {
      // Check if it's an object
      if (typeof site === 'object' && site !== null) {
        // Nested structure: {siteData: {...}, siteNum: "..."}
        if (site.siteData && site.siteNum) {
          const foundSite = this.filteredSites.find(s => s.siteNum === site.siteNum);
          if (foundSite) {
            return foundSite;
          }
          return null;
        }

        // Direct object with siteName
        if (site.siteName) {
          return site;
        }
      }

      // It's an ID string - look it up
      if (typeof site === 'string') {
        const foundSite = this.filteredSites.find(s => s.siteNum === site);
        if (foundSite) {
          return foundSite;
        }
        return null;
      }

      return null;
    }).filter(site => site !== null); // Remove any nulls

    return result;
  }

  /**
   * Extract owner/user field for form binding
   * Backend now returns hydrated user objects: { userId: "email", userData: { email, given_name, ... }, permissions: {...} }
   * Form needs the full user object for autocomplete to match and display properly
   * @param owner - Can be:
   *   - Hydrated user object: {userId: "email", userData: {...}}
   *   - Legacy string: "email@example.com"
   *   - null/undefined
   * @returns Full user object for form, or null
   */
  extractOwnerForForm(owner: any): any | null {
    if (!owner) return null;

    // Extract email from various formats
    let email: string | null = null;
    if (typeof owner === 'string') {
      email = owner;
    } else if (typeof owner === 'object') {
      email = owner.userId || owner.email || (owner.userData && owner.userData.email) || null;
    }

    // Always look up the user object from filteredUsers so the autocomplete
    // gets the same object reference used in its options list
    if (email && this.filteredUsers.length > 0) {
      const foundUser = this.filteredUsers.find((u: any) => u.email === email || u.userId === email);
      if (foundUser) {
        return foundUser;
      }
    }

    // If we have an email but couldn't find the user, return a minimal object
    if (email) {
      return { email: email, shortFriendlyName: email, friendlyName: email };
    }

    return null;
  }

  /**
   * Extract user for form binding (for autocomplete fields that need full user objects)
   * Looks up the user object from the filteredPartyUsers list
   * @param user - Can be:
   *   - Hydrated user object: {email: "...", userData: {...}}
   *   - Email string: "email@example.com"
   *   - null/undefined
   * @returns Full user object for form, or null
   */
  extractUserForForm(user: any): any | null {
    if (!user) return null;

    // Extract email from various formats
    let email: string | null = null;
    if (typeof user === 'string') {
      email = user;
    } else if (typeof user === 'object') {
      email = user.email || user.userId || (user.userData && user.userData.email) || null;
    }

    // Always look up the user object from filteredUsers so the autocomplete
    // gets the same object reference used in its options list
    if (email && this.filteredUsers.length > 0) {
      const foundUser = this.filteredUsers.find((u: any) => u.email === email || u.userId === email);
      if (foundUser) {
        return foundUser;
      }
    }

    // If we have an email but couldn't find the user, return a minimal object
    if (email) {
      return { email: email, shortFriendlyName: email };
    }

    return null;
  }

  /**
   * Extract person for form binding (for autocomplete fields that need full person objects)
   * Looks up the person object from the filteredPeople list
   * @param person - Can be:
   *   - Person object: {contactNum: "...", fullName: "...", ...}
   *   - contactNum string: "PERSON123"
   *   - null/undefined
   * @returns Full person object for form, or null
   */
  extractPersonForForm(person: any): any | null {
    if (!person) return null;

    // Extract contactNum from various formats
    let contactNum: string | null = null;
    if (typeof person === 'string') {
      contactNum = person;
    } else if (typeof person === 'object') {
      contactNum = person.contactNum || null;
    }

    // Always look up the person object from filteredPeople so the autocomplete
    // gets the same object reference used in its options list
    if (contactNum && this.filteredPeople.length > 0) {
      const foundPerson = this.filteredPeople.find((p: any) => p.contactNum === contactNum);
      if (foundPerson) {
        return foundPerson;
      }
    }

    // If we have a contactNum but couldn't find the person, return a minimal object
    if (contactNum) {
      return { contactNum: contactNum, fullName: contactNum };
    }

    return null;
  }

  // Filter methods - Most inherit from Entity base class
  // Only override if you need custom behavior
  filterContractTypes(event: any) {
    const filteredList = this.contractTypes.filter(option =>
      option.toLowerCase().includes(event.query.toLowerCase())
    );
    this.filteredContractTypes = filteredList;
  }

  filterDepartments(event: any) {
    const filteredList = this.departments.filter(option =>
      option.toLowerCase().includes(event.query.toLowerCase())
    );
    this.filteredDepartments = filteredList;
  }

  // Override filter methods to work with Material Design autocomplete
  // Material sends Event objects with event.target.value, not { query: string } like PrimeNG
  filterContracts(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.contractService.allContracts$,
      'filteredContracts',
      'nameOfContract',
      query
    );
  }

  filterParties(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.partyService.allParties$,
      'filteredParties',
      'name',
      query
    );
  }

  filterUsers(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.usersService.allUsers$,
      'filteredUsers',
      'friendlyName',
      query
    );
  }

  filterPeople(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.peopleService.allPeople$,
      'filteredPeople',
      'fullName',
      query
    );
  }

  filterLegalEntities(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.legalEntityService.allLegalEntities$,
      'filteredLegalEntities',
      'legalEntityName',
      query
    );
  }

  // BAA/COI selector helpers
  get filteredBaaOptions(): any[] {
    if (!this.baaSearchFilter) return this.filteredBaas;
    const q = this.baaSearchFilter.toLowerCase();
    return this.filteredBaas.filter((baa: any) =>
      (baa.name || '').toLowerCase().includes(q) ||
      (baa.coveredEntity || '').toLowerCase().includes(q) ||
      (baa.baaNum || '').toLowerCase().includes(q)
    );
  }

  get filteredCoiOptions(): any[] {
    if (!this.coiSearchFilter) return this.filteredCois;
    const q = this.coiSearchFilter.toLowerCase();
    return this.filteredCois.filter((coi: any) =>
      (coi.name || '').toLowerCase().includes(q) ||
      (coi.insuranceProvider || '').toLowerCase().includes(q) ||
      (coi.coiNum || '').toLowerCase().includes(q)
    );
  }

  // ---- In-panel search for the multi-select pickers -------------------------
  // Each getter filters its full option list by what's typed in that panel's
  // search box. The searchable text is always exactly what the option renders,
  // so a user typing letters finds what they can see.

  /**
   * Case-insensitive "contains" filter shared by the pickers below.
   *
   * Options that are already SELECTED always survive the filter, even when they
   * don't match the query. mat-select rebuilds its selection model from the
   * options currently rendered (`_setSelectionByValue`), so an option filtered
   * out of the list is dropped from the selection — the trigger text loses it
   * and the next click writes a form value missing every previously-picked item.
   * Keeping selected options visible also lets the user un-pick them mid-search.
   *
   * @param selected   current form-control value for this picker (array or null)
   * @param isSame     how a selected VALUE compares to an option — mirrors the
   *                   picker's `compareWith`, or the option's value when the
   *                   template binds something other than the object itself.
   */
  private matchOptions<T>(
    list: T[],
    query: string,
    textOf: (item: T) => string,
    selected: any = null,
    isSame: (selectedValue: any, item: T) => boolean = (selectedValue, item) => selectedValue === item
  ): T[] {
    const source = list ?? [];
    const q = (query || '').toLowerCase().trim();
    if (!q) return source;
    const picked: any[] = Array.isArray(selected) ? selected : [];
    return source.filter(item =>
      (textOf(item) || '').toLowerCase().includes(q) ||
      picked.some(value => isSame(value, item))
    );
  }

  get filteredSiteOptions(): any[] {
    return this.matchOptions(
      this.filteredSites,
      this.siteSearchFilter,
      (s: any) => s?.name,
      this.form.get('sites')?.value
    );
  }

  get filteredDepartmentOptions(): any[] {
    return this.matchOptions(
      this.departments,
      this.departmentSearchFilter,
      (d: any) => d,
      this.form.get('departments')?.value
    );
  }

  get filteredRegulatoryOptions(): any[] {
    return this.matchOptions(
      this.regulatoryValues,
      this.regulatorySearchFilter,
      (r: any) => r,
      this.form.get('regulatory')?.value
    );
  }

  get otherPartiesOptions(): any[] {
    // partyLabel() is the same function behind the | partyLabel pipe in the
    // template, so the search matches the rendered label exactly.
    return this.matchOptions(
      this.allPartiesList,
      this.otherPartiesSearchFilter,
      partyLabel,
      this.form.get('otherParties')?.value,
      this.comparePartiesFn
    );
  }

  get subcontractorOptions(): any[] {
    // This picker's option value is the vendorNum, not the party object.
    return this.matchOptions(
      this.allPartiesList,
      this.subcontractorSearchFilter,
      partyLabel,
      this.form.get('subcontractorVendorNums')?.value,
      (vendorNum: any, party: any) => vendorNum === party?.vendorNum
    );
  }

  /**
   * Opens/closes housekeeping for a picker that has an in-panel search box:
   * focus the box on open, clear its query on close.
   *
   * The focus step is what makes the box usable. mat-select keeps keyboard focus
   * on the panel when it opens, and it listens for keydown there to run its own
   * single-letter typeahead — so a user who opened the list and started typing
   * saw the highlight jump around and nothing appear in the search box. The box
   * only worked if you first noticed it and clicked into it.
   */
  onPickerOpened(opened: boolean, filterProp: PickerFilterKey, select: MatSelect): void {
    if (!opened) {
      this[filterProp] = '';
      return;
    }
    // The panel is rendered from an overlay template, so it isn't in the DOM yet
    // when openedChange fires.
    setTimeout(() => {
      const input = select.panel?.nativeElement?.querySelector('.select-search-input') as HTMLInputElement | null;
      input?.focus();
    });
  }

  /**
   * Keeps typing inside a picker's search box instead of feeding it to
   * mat-select's own keyboard handling (typeahead on letters, select on Space).
   *
   * Escape, Tab and the arrow keys are deliberately allowed through so the panel
   * still closes and the highlight still moves while the cursor stays in the box.
   * Enter is NOT: after the list is filtered mat-select's highlight can still sit
   * on whatever it picked when the panel opened, so Enter would toggle an option
   * the user never looked at.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    const passThrough = ['Escape', 'ArrowDown', 'ArrowUp', 'Tab'];
    if (!passThrough.includes(event.key)) {
      event.stopPropagation();
    }
  }

  compareBaaFn(a: any, b: any): boolean {
    if (!a || !b) return false;
    const aNum = typeof a === 'string' ? a : (a.baaData?.baaNum || a.baaNum);
    const bNum = typeof b === 'string' ? b : (b.baaData?.baaNum || b.baaNum);
    return aNum === bNum;
  }

  compareCoiFn(a: any, b: any): boolean {
    if (!a || !b) return false;
    const aNum = typeof a === 'string' ? a : (a.coiData?.coiNum || a.coiNum);
    const bNum = typeof b === 'string' ? b : (b.coiData?.coiNum || b.coiNum);
    return aNum === bNum;
  }

  displayBaaName(baa: any): string {
    if (!baa) return '';
    if (typeof baa === 'string') return baa;
    // Handle wrapped format (baaData wrapper from backend)
    const data = baa.baaData || baa;
    const parts: string[] = [];
    if (data.baaId) parts.push(data.baaId);
    const coveredEntity = data.coveredEntityFriendlyName || data.coveredEntity?.name || (typeof data.coveredEntity === 'string' ? data.coveredEntity : '');
    if (coveredEntity) parts.push(coveredEntity);
    const bizAssociate = data.businessAssociateNameFriendlyName || data.businessAssociateName?.name || (typeof data.businessAssociateName === 'string' ? data.businessAssociateName : '');
    if (bizAssociate) parts.push(bizAssociate);
    return parts.join(' - ') || '';
  }

  displayCoiName(coi: any): string {
    if (!coi) return '';
    if (typeof coi === 'string') return coi;
    // Handle wrapped format (coiData wrapper from backend)
    const data = coi.coiData || coi;
    const parts: string[] = [];
    if (data.coiId) parts.push(data.coiId);
    const insuredName = data.insuredPartyFriendlyName || data.insuredParty?.name || (typeof data.insuredParty === 'string' ? data.insuredParty : '');
    if (insuredName) parts.push(insuredName);
    return parts.join(' - ') || '';
  }

  extractDirectBaaForUI(directBaa: any): any {
    if (!directBaa) return null;
    // Handle wrapped format: { baaData: {...}, baaNum: "..." }
    const data = directBaa.baaData || directBaa;
    const baaNum = typeof directBaa === 'string' ? directBaa : (data.baaNum || directBaa.baaNum);
    if (baaNum && this.filteredBaas?.length) {
      return this.filteredBaas.find((b: any) => b.baaNum === baaNum) || null;
    }
    // Fallback: unwrap if needed so display functions work
    return typeof directBaa === 'object' ? data : null;
  }

  extractDirectCoiForUI(directCoi: any): any {
    if (!directCoi) return null;
    // Handle wrapped format: { coiData: {...}, coiNum: "..." }
    const data = directCoi.coiData || directCoi;
    const coiNum = typeof directCoi === 'string' ? directCoi : (data.coiNum || directCoi.coiNum);
    if (coiNum && this.filteredCois?.length) {
      return this.filteredCois.find((c: any) => c.coiNum === coiNum) || null;
    }
    // Fallback: unwrap if needed so display functions work
    return typeof directCoi === 'object' ? data : null;
  }

  // Reset filter methods - called on focus to show all options
  resetPartiesFilter(): void {
    this.partyService.allParties$.pipe(first()).subscribe(parties => {
      this.filteredParties = parties;
    });
  }

  resetUsersFilter(): void {
    this.usersService.allUsers$.pipe(first()).subscribe(users => {
      this.filteredUsers = users;
    });
  }

  resetPeopleFilter(): void {
    this.peopleService.allPeople$.pipe(first()).subscribe(people => {
      this.filteredPeople = people;
    });
  }

  resetLegalEntitiesFilter(): void {
    this.legalEntityService.allLegalEntities$.pipe(first()).subscribe(entities => {
      this.filteredLegalEntities = entities;
    });
  }

  resetContractsFilter(): void {
    this.contractService.allContracts$.pipe(first()).subscribe(contracts => {
      this.filteredContracts = contracts;
    });
  }

  onSelectLegalEntity() { }
  addedOtherParty() { }

  autoRenewToggleChanged(event: any) {
    this.form.get('autorenew')?.setValue(event.checked);
  }

  toggleContractViewer() {
    this.showDocumentViewer = !this.showDocumentViewer;
  }

  toggleAdditionalDetails() {
    this.showAdditionalDetails = !this.showAdditionalDetails;
  }

  toggleAISummary() {
    this.showAISummary = !this.showAISummary;
  }

  cancelSubmit() {
    this.router.navigateByUrl('/securehome/contracts');
  }

  async uploadContractFileToS3(file: File, contractId: string): Promise<string> {
    try {
      // 1. Generate unique filename
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `contracts/${contractId}/${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;

      // 2. Get pre-signed URL
      const preSignedUrlResponse = await firstValueFrom(
        this.fileUploadService.getPresignedUrl(uniqueFileName)
      );
      const preSignedUrl = preSignedUrlResponse.presignedUrl;
      
      // 3. Upload to S3
      await firstValueFrom(
        this.fileUploadService.uploadFileToS3(preSignedUrl, file)
      );

      // 4. Return the direct link (without query params)
      return preSignedUrl.split('?')[0];
      
    } catch (error) {
      console.error('S3 upload failed:', error);
      throw error;
    }
  }




  async onSubmit() {
    if (!this.isFormValid()) {
      alert(this.getValidationMessage());
      return;
    }

    this.saving = true;
    const contractToSave: Contract = this.form.value;

    // Ensure status is never null - DynamoDB GSI requires string value
    if (!contractToSave.status) {
      contractToSave.status = 'draft';
    }

    // For EDIT mode, contractId is required; for CREATE, backend generates it
    if (this.isEditMode && !contractToSave.contractId) {
      alert('Contract ID is missing for edit operation');
      this.saving = false;
      return;
    }

    // Extract only IDs from object fields (not entire objects)
    // Party/Vendor fields - extract vendorNum
    if (contractToSave.directVendor && typeof contractToSave.directVendor === 'object') {
      contractToSave.directVendor = contractToSave.directVendor.vendorNum || contractToSave.directVendor;
    }
    if (contractToSave.parentParty && typeof contractToSave.parentParty === 'object') {
      contractToSave.parentParty = contractToSave.parentParty.vendorNum || contractToSave.parentParty;
    }
    if (contractToSave.party && typeof contractToSave.party === 'object') {
      contractToSave.party = contractToSave.party.vendorNum || contractToSave.party;
    }
    if (contractToSave.otherParties && Array.isArray(contractToSave.otherParties)) {
      contractToSave.otherParties = contractToSave.otherParties.map(p =>
        typeof p === 'object' && p.vendorNum ? p.vendorNum : p
      );
    }

    // User/People fields - extract userId/email (backend now expects email strings, returns hydrated objects)
    // Extract in order of preference: userId (from hydrated), userNum, email
    if (contractToSave.internalSignatory && typeof contractToSave.internalSignatory === 'object') {
      contractToSave.internalSignatory = contractToSave.internalSignatory.userId || contractToSave.internalSignatory.userNum || contractToSave.internalSignatory.email || contractToSave.internalSignatory;
    }
    if (contractToSave.primaryResponsibleParty && typeof contractToSave.primaryResponsibleParty === 'object') {
      contractToSave.primaryResponsibleParty = contractToSave.primaryResponsibleParty.userId || contractToSave.primaryResponsibleParty.userNum || contractToSave.primaryResponsibleParty.email || contractToSave.primaryResponsibleParty;
    }
    if (contractToSave.yourContractCoordinator && typeof contractToSave.yourContractCoordinator === 'object') {
      contractToSave.yourContractCoordinator = contractToSave.yourContractCoordinator.userId || contractToSave.yourContractCoordinator.userNum || contractToSave.yourContractCoordinator.email || contractToSave.yourContractCoordinator;
    }
    if (contractToSave.partiesSignatory && typeof contractToSave.partiesSignatory === 'object') {
      contractToSave.partiesSignatory = contractToSave.partiesSignatory.contactNum || contractToSave.partiesSignatory;
    }
    if (contractToSave.partyContractCoordinator && typeof contractToSave.partyContractCoordinator === 'object') {
      contractToSave.partyContractCoordinator = contractToSave.partyContractCoordinator.contactNum || contractToSave.partyContractCoordinator;
    }

    // Owner fields - map from form fields to contract fields and extract userId/email
    // Backend now auto-hydrates these fields and expects email strings on save
    const primaryOwner = this.form.get('primaryOwner')?.value;
    if (primaryOwner && typeof primaryOwner === 'object') {
      (contractToSave as any).primaryOwner = primaryOwner.userId || primaryOwner.userNum || primaryOwner.email || primaryOwner;
    } else if (primaryOwner) {
      (contractToSave as any).primaryOwner = primaryOwner;
    }

    const secondaryOwner = this.form.get('secondaryOwner')?.value;
    if (secondaryOwner && typeof secondaryOwner === 'object') {
      (contractToSave as any).secondaryOwner = secondaryOwner.userId || secondaryOwner.userNum || secondaryOwner.email || secondaryOwner;
    } else if (secondaryOwner) {
      (contractToSave as any).secondaryOwner = secondaryOwner;
    }

    const tertiaryOwner = this.form.get('tertiaryOwner')?.value;
    if (tertiaryOwner && typeof tertiaryOwner === 'object') {
      (contractToSave as any).tertiaryOwner = tertiaryOwner.userId || tertiaryOwner.userNum || tertiaryOwner.email || tertiaryOwner;
    } else if (tertiaryOwner) {
      (contractToSave as any).tertiaryOwner = tertiaryOwner;
    }

    // Legal Entities - convert single entity to array and extract legalEntityNum
    if (contractToSave.legalEntities) {
      if (!Array.isArray(contractToSave.legalEntities) && typeof contractToSave.legalEntities === 'object' && (contractToSave.legalEntities as any).legalEntityNum) {
        // Single entity object selected from UI - convert to array of IDs
        contractToSave.legalEntities = [(contractToSave.legalEntities as any).legalEntityNum];
      } else if (Array.isArray(contractToSave.legalEntities)) {
        // Already an array - extract IDs
        contractToSave.legalEntities = contractToSave.legalEntities.map((le: any) =>
          typeof le === 'object' && le.legalEntityNum ? le.legalEntityNum : le
        );
      } else {
        // Single ID or other format - wrap in array
        contractToSave.legalEntities = [contractToSave.legalEntities as any];
      }
    } else {
      contractToSave.legalEntities = [];
    }

    // Sites - extract siteNum
    if (contractToSave.sites && Array.isArray(contractToSave.sites)) {
      contractToSave.sites = contractToSave.sites.map((s: any) =>
        typeof s === 'object' && s.siteNum ? s.siteNum : s
      );
    }

    // Sub-Contractors - extract vendorNum
    if (contractToSave.subcontractorVendorNums && Array.isArray(contractToSave.subcontractorVendorNums)) {
      contractToSave.subcontractorVendorNums = contractToSave.subcontractorVendorNums.map((sc: any) =>
        typeof sc === 'object' && sc.vendorNum ? sc.vendorNum : sc
      );
    }

    // Master Contract - extract contractId
    if (contractToSave.masterContract && typeof contractToSave.masterContract === 'object') {
      contractToSave.masterContract = (contractToSave.masterContract as any).contractId || contractToSave.masterContract;
    }

    // BAA - extract baaNum (partition key)
    if (contractToSave.directBaa && typeof contractToSave.directBaa === 'object') {
      contractToSave.directBaa = (contractToSave.directBaa as any).baaNum || (contractToSave.directBaa as any).baaId || contractToSave.directBaa;
    }

    // COI - extract coiNum (partition key)
    if (contractToSave.directCoi && typeof contractToSave.directCoi === 'object') {
      contractToSave.directCoi = (contractToSave.directCoi as any).coiNum || (contractToSave.directCoi as any).coiId || contractToSave.directCoi;
    }

    // Handle file URL for contract
    // Priority: 1) Reuse AI upload URL, 2) Upload new file, 3) Keep existing URL
    if (this.aiUploadedS3Url) {
      // Reuse the S3 URL from AI analysis (avoids duplicate upload)
      contractToSave.linkToContract = this.aiUploadedS3Url;
      // console.log('Reusing AI-uploaded S3 URL:', this.aiUploadedS3Url);
    } else if (this.uploadedFile) {
      // Upload new file to S3 (no AI analysis was run)
      try {
        const uploadPath = contractToSave.contractId || `temp-${Date.now()}`;
        const fileUrl = await this.uploadContractFileToS3(this.uploadedFile, uploadPath);
        contractToSave.linkToContract = fileUrl;
      } catch (error) {
        console.error('Failed to upload file:', error);
        this.saving = false;
        alert('Failed to upload contract file. Please try again.');
        return;
      }
    } else if (this.currentDocument?.directLink) {
      // Keep existing file URL if no new file uploaded
      contractToSave.linkToContract = this.currentDocument?.directLink;
    }

    const action = this.isEditMode ? DatabaseAction.Edit : DatabaseAction.Create;

    // For replace mode, clear both IDs — backend creates a brand new record.
    // Old and new contracts are linked via versionList after creation.
    if (this.isReplaceMode && this.sourceContractForReplace) {
      delete (contractToSave as any).contractId;
      delete (contractToSave as any).contractNum;
    }

    // Set creationDate only when creating a new contract (not when editing)
    if (action === DatabaseAction.Create && !contractToSave.creationDate) {
      contractToSave.creationDate = new Date().toISOString();
    }

    // console.log('[Contract Save] Payload being sent:', {
    //   action: action,
    //   isEditMode: this.isEditMode,
    //   contractId: contractToSave.contractId,
    //   contractNum: contractToSave.contractNum,
    //   directVendor: contractToSave.directVendor,
    //   legalEntities: contractToSave.legalEntities,
    //   otherParties: contractToSave.otherParties,
    //   fullPayload: JSON.stringify(contractToSave, null, 2)
    // });

    // Use createReplacementContract for replace mode, writeContract for normal create/edit
    const saveObservable = this.isReplaceMode
      ? this.contractService.createReplacementContract(contractToSave)
      : this.contractService.writeContract(contractToSave, 'active', action);

    saveObservable.subscribe({
      next: async (response) => {
        // CRITICAL: Update contractId and contractNum from response if the backend returned them
        // This ensures we use the database-confirmed IDs, not locally generated ones
        const confirmedContractId = response?.contract?.contractId ||
                                     response?.contractId ||
                                     contractToSave.contractId;
        const confirmedContractNum = response?.contract?.contractNum ||
                                      response?.contractNum ||
                                      contractToSave.contractNum;

        // Update contractToSave with confirmed IDs before any sync operations
        if (confirmedContractId !== contractToSave.contractId) {
          // console.log(`ContractId updated from local ${contractToSave.contractId} to confirmed ${confirmedContractId}`);
          contractToSave.contractId = confirmedContractId;
        }
        if (confirmedContractNum !== contractToSave.contractNum) {
          // console.log(`ContractNum updated from local ${contractToSave.contractNum} to confirmed ${confirmedContractNum}`);
          contractToSave.contractNum = confirmedContractNum;
        }

        // After contract is saved, update relationships (now using confirmed IDs)
        try {
          await this.updateRelationships(contractToSave);
        } catch (error) {
          console.error('Failed to update relationships (contract saved but relationships may be inconsistent):', error);
        }

        // Sync BAA/COI associated contracts
        try {
          await this.syncBaaCoiAssociatedContracts(contractToSave);
        } catch (error) {
          console.error('Failed to sync BAA/COI associated contracts:', error);
        }

        // Sync associated contracts based on primary party (now using confirmed IDs)
        try {
          await this.syncAssociatedContractsForPrimaryParty(contractToSave);
        } catch (error) {
          console.error('Failed to sync associated contracts (contract saved but associations may be inconsistent):', error);
        }

        // Replace mode: supersede old contract and update versionList on both
        if (this.isReplaceMode && this.sourceContractForReplace) {
          try {
            await this.finalizeReplacement(confirmedContractNum, confirmedContractId);
          } catch (error) {
            console.error('Failed to finalize replacement (new version saved but old may not be superseded):', error);
          }
        }

        this.saving = false;

        // Use the confirmed contractNum for navigation
        const savedContractNum = confirmedContractNum;

        // Navigate to the contract-form to display the saved/updated contract
        this.router.navigate(['/securehome/contract', savedContractNum]);
      },
      error: (err) => {
        console.error('Failed to save contract:', err);
        this.saving = false;
        alert('Failed to save contract. Please try again.');
      }
    });
  }

  /**
   * Updates all relationships for the contract after saving
   * Compares original values with new values and calls relationship API
   */
  private async updateRelationships(contract: Contract): Promise<void> {
    const contractRef = {
      type: 'contract' as const,
      id: contract.contractId!  // CRITICAL: Use contractId, not contractNum (versions change)
    };

    // Track relationship fields that need updating
    // NOTE: User fields (primaryResponsibleParty, internalSignatory, yourContractCoordinator)
    //       and Person fields (partiesSignatory, partyContractCoordinator) are NOT tracked at this time
    const relationshipFields = [
      { field: 'directVendor', oldValue: this.originalContract.directVendor, newValue: contract.directVendor },
      { field: 'parentParty', oldValue: this.originalContract.parentParty, newValue: contract.parentParty },
      { field: 'otherParties', oldValue: this.originalContract.otherParties, newValue: contract.otherParties },
      { field: 'legalEntities', oldValue: this.originalContract.legalEntities, newValue: contract.legalEntities },
      { field: 'sites', oldValue: this.originalContract.sites, newValue: contract.sites },
      { field: 'masterContract', oldValue: this.originalContract.masterContract, newValue: contract.masterContract },
      { field: 'directBaa', oldValue: this.originalContract.directBaa, newValue: contract.directBaa },
      { field: 'directCoi', oldValue: this.originalContract.directCoi, newValue: contract.directCoi }
    ];

    // Update each relationship field that changed
    for (const { field, oldValue, newValue } of relationshipFields) {
      // Skip if values are the same
      if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
        continue;
      }

      // Update the relationship
      await this.relationshipService.update(
        contractRef,
        field,
        oldValue,
        newValue
      );
    }
  }

  /**
   * When directBaa or directCoi changes, add/remove this contract's ID
   * from the BAA/COI's contracts array.
   */
  private async syncBaaCoiAssociatedContracts(contract: Contract): Promise<void> {
    const contractId = contract.contractId;
    if (!contractId) return;

    const oldBaa = this.originalContract?.directBaa;
    const newBaa = contract.directBaa;
    const oldCoi = this.originalContract?.directCoi;
    const newCoi = contract.directCoi;

    // Helper: extract string ID
    const extractId = (val: any, key: string): string | null => {
      if (!val) return null;
      if (typeof val === 'string') return val;
      return val[key] || null;
    };

    const oldBaaNum = extractId(oldBaa, 'baaNum');
    const newBaaNum = extractId(newBaa, 'baaNum');
    const oldCoiNum = extractId(oldCoi, 'coiNum');
    const newCoiNum = extractId(newCoi, 'coiNum');

    // Remove contract from old BAA if it changed
    if (oldBaaNum && oldBaaNum !== newBaaNum) {
      try {
        const oldBaaObj = await this.baaService.getBaaByNumber(oldBaaNum).toPromise();
        if (oldBaaObj) {
          const contracts = (oldBaaObj.contracts || []).filter((id: string) => id !== contractId);
          await this.baaService.updateBaaAttribute(oldBaaNum, 'contracts', contracts).toPromise();
        }
      } catch (e) { console.error('Failed to remove contract from old BAA:', e); }
    }

    // Add contract to new BAA
    if (newBaaNum) {
      try {
        const newBaaObj = await this.baaService.getBaaByNumber(newBaaNum).toPromise();
        if (newBaaObj) {
          const contracts = [...new Set([...(newBaaObj.contracts || []), contractId])];
          await this.baaService.updateBaaAttribute(newBaaNum, 'contracts', contracts).toPromise();
        }
      } catch (e) { console.error('Failed to add contract to BAA:', e); }
    }

    // Remove contract from old COI if it changed
    if (oldCoiNum && oldCoiNum !== newCoiNum) {
      try {
        const oldCoiObj = await this.coiService.getCoiByNumber(oldCoiNum).toPromise();
        if (oldCoiObj) {
          const contracts = (oldCoiObj.contracts || []).filter((id: string) => id !== contractId);
          await this.coiService.updateCoiAttribute(oldCoiNum, 'contracts', contracts).toPromise();
        }
      } catch (e) { console.error('Failed to remove contract from old COI:', e); }
    }

    // Add contract to new COI
    if (newCoiNum) {
      try {
        const newCoiObj = await this.coiService.getCoiByNumber(newCoiNum).toPromise();
        if (newCoiObj) {
          const contracts = [...new Set([...(newCoiObj.contracts || []), contractId])];
          await this.coiService.updateCoiAttribute(newCoiNum, 'contracts', contracts).toPromise();
        }
      } catch (e) { console.error('Failed to add contract to COI:', e); }
    }
  }

  /**
   * Syncs associated contracts based on primary party (directVendor).
   * When contracts share the same primary party, they become associated with each other.
   *
   * This method:
   * 1. Extracts vendorNum from old and new directVendor
   * 2. Calls ContractService.syncAssociatedContracts to update other contracts
   * 3. Updates this contract's associatedContracts with the returned list
   */
  private async syncAssociatedContractsForPrimaryParty(contract: Contract): Promise<void> {
    if (!contract.contractId || !contract.directVendor) {
      return;
    }

    // Helper to extract vendorNum from various formats
    const extractVendorNum = (vendor: any): string | null => {
      if (!vendor) return null;
      if (typeof vendor === 'string') return vendor;
      return vendor.vendorNum || null;
    };

    const newVendorNum = extractVendorNum(contract.directVendor);
    const oldVendorNum = extractVendorNum(this.originalContract?.directVendor);

    if (!newVendorNum) {
      console.warn('syncAssociatedContractsForPrimaryParty: No vendorNum found for directVendor');
      return;
    }

    // Get the list of associated contractIds from contracts with the same primary party
    const associatedContractIds = this.contractService.getAssociatedContractIds(
      newVendorNum,
      contract.contractId
    );

    // Update the contract with the associated contracts list
    contract.associatedContracts = [...new Set(associatedContractIds)];

    // Call the sync method to update other contracts
    // Only pass oldVendorNum if it actually changed
    const oldVendorToPass = oldVendorNum !== newVendorNum ? oldVendorNum : undefined;

    return new Promise((resolve, reject) => {
      this.contractService.syncAssociatedContracts(
        contract.contractId!,
        newVendorNum,
        oldVendorToPass || undefined
      ).subscribe({
        next: (result) => {
          // console.log(`Associated contracts sync complete: ${result.updated} contracts updated`);

          // Write this contract again with the updated associatedContracts
          if (associatedContractIds.length > 0 || (oldVendorToPass && contract.associatedContracts?.length === 0)) {
            this.contractService.writeContract(contract, 'active', DatabaseAction.Edit).subscribe({
              next: () => {
                // console.log('Contract updated with associatedContracts');
                resolve();
              },
              error: (err) => {
                console.error('Failed to update contract with associatedContracts:', err);
                resolve(); // Don't fail the whole operation
              }
            });
          } else {
            resolve();
          }
        },
        error: (err) => {
          console.error('Failed to sync associated contracts:', err);
          resolve(); // Don't fail the whole operation
        }
      });
    });
  }

  generateContractId(): string {
    return generateContractId();
  }

  /**
   * Open the Add New Party dialog
   * After a party is added, refresh the parties list so it appears in autocomplete
   */
  openAddPartyDialog(): void {
    const dialogRef = this.dialog.open(NewPartyDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Trigger the refresh, then wait for the re-stream to FINISH. `skip(1),
        // first()` used to take the first emission after the refresh started —
        // which is page 1, so adding a party could shrink the pickers back to
        // ~100 rows. settledCache() waits for the last page instead.
        this.partyService.refreshAllPartiesRecords().subscribe();
        settledCache(this.partyService.allParties$).then(parties => {
          this.filteredParties = parties;
          // Keep the multi-select pickers in sync so a just-added party is
          // selectable there too, not just in the Primary Party autocomplete.
          this.allPartiesList = parties;
          this.cdr.detectChanges();
        });
      }
    });
  }

  /**
   * Open the Add New Person dialog
   * After a person is added, refresh the people list so it appears in autocomplete
   */
  /**
   * Finalizes a contract replacement:
   * 1. Supersedes the old contract (marks inactive, updates status)
   * 2. Updates versionList on both old and new contracts
   */
  private async finalizeReplacement(newContractNum: string, newContractId: string): Promise<void> {
    const source = this.sourceContractForReplace!;
    const oldContractNum = source.contractNum!;
    const oldContractId = source.contractId!;

    // Build the version list: existing versions + old contract + new contract
    const existingVersionList = source.versionList || [];
    const updatedVersionList = [...existingVersionList];

    // Add old contract to version list if not already there
    if (!updatedVersionList.includes(oldContractNum)) {
      updatedVersionList.push(oldContractNum);
    }
    // Add new contract to version list
    if (newContractNum && !updatedVersionList.includes(newContractNum)) {
      updatedVersionList.push(newContractNum);
    }

    // Determine version numbers based on position in the version list
    const oldVersionNumber = source.version || updatedVersionList.indexOf(oldContractNum) + 1 || 1;
    const newVersionNumber = oldVersionNumber + 1;

    // Step 1: Supersede the old contract (uses its own contractId + contractNum)
    const oldContractUpdate: Contract = {
      ...source,
      activeContract: false,
      status: 'Superseded',
      version: oldVersionNumber,
      versionList: updatedVersionList
    };

    await firstValueFrom(
      this.contractService.writeContract(oldContractUpdate, 'active', DatabaseAction.Edit)
    );

    // Step 2: Update the new contract with the versionList and version number
    if (newContractNum && newContractId) {
      const newContractUpdate: any = {
        contractId: newContractId,
        contractNum: newContractNum,
        version: newVersionNumber,
        versionList: updatedVersionList
      };
      await firstValueFrom(
        this.contractService.writeContract(newContractUpdate, 'active', DatabaseAction.Edit)
      );
    }
  }

  openAddPersonDialog(): void {
    const dialogRef = this.dialog.open(NewPersonDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Same as the party case above: wait for the whole re-stream, not page 1.
        this.peopleService.refreshAllPeopleRecords().subscribe();
        settledCache(this.peopleService.allPeople$).then(people => {
          this.filteredPeople = people;
          this.cdr.detectChanges();
        });
      }
    });
  }

}